/**
 * <venoa-scent-story> – steuert die Duft-Sequenz (sections/venoa-scent-story.liquid)
 * auf Startseite und Produktseite.
 *
 * Darstellungen:
 * - Bühne (data-mode="stage"; Desktop ab 990 px, genug Höhe): Bild und Text
 *   stehen gemeinsam und mittig (sticky), darunter liegt je Duft eine Station
 *   Scrollweg. Kommt das Scrollen zur Ruhe, gleitet die Ansicht weich in die
 *   nächste Leseposition (540–900 ms); jede neue Eingabe unterbricht sofort.
 * - Antippen (data-mode="tap"; Smartphone): Bild, Duftnavigation und Text
 *   stehen als kompakte Gruppe im normalen Seitenfluss. Düfte wechseln nur
 *   durch Antippen der Navigation – Scrollen wechselt nichts. Kommt das
 *   Scrollen nahe der Leseposition zur Ruhe (Finger losgelassen, Schwung
 *   ausgelaufen), gleitet die Gruppe einmal weich in die Bildschirmmitte
 *   (500–800 ms); wer weiterscrollt, wird nie festgehalten oder zurückgezogen.
 * - Klassisch (≥ 990 px mit wenig Höhe), Liste (Tablet, ohne JavaScript).
 *
 * Duftwechsel (Bühne und Antippen) laufen zeitgesteuert und immer bis zum
 * Ende: Text in ~290 ms (CSS, .is-text), Bild als kurze Enthüllung des
 * fertig geladenen Zielbilds (~0,5 s), Farbschein ruhig (CSS). Ein neuer
 * Wechsel übernimmt vom aktuellen Stand – der letzte gewinnt, es bleibt nie
 * ein Mischbild stehen.
 *
 * Nur diese Section ist betroffen. Die Story wählt nie eine Produktvariante
 * und löst keine Ereignisse des Kaufbereichs aus. Bei „weniger Bewegung“
 * entfallen Gleiten, Ausrichten und Übergänge.
 */
if (!customElements.get('venoa-scent-story')) {
  const WIDE_QUERY = '(min-width: 990px)';
  const WIDE_STAGE_QUERY = '(min-width: 990px) and (min-height: 560px)';
  const PHONE_QUERY = '(max-width: 749px)';
  const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

  /** Kubische Bézierkurve wie in CSS (x1, y1, x2, y2) → Funktion t ↦ Fortschritt. */
  const bezier = (x1, y1, x2, y2) => {
    const sample = (a1, a2, t) => ((1 - 3 * a2 + 3 * a1) * t + (3 * a2 - 6 * a1)) * t * t + 3 * a1 * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let lo = 0;
      let hi = 1;
      let t = x;
      for (let i = 0; i < 24; i += 1) {
        const value = sample(x1, x2, t);
        if (Math.abs(value - x) < 1e-4) break;
        if (value < x) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
      }
      return sample(y1, y2, t);
    };
  };

  // Weicher Anfang, langes sanftes Abbremsen, kein Überschwingen.
  const glideEase = bezier(0.38, 0.02, 0.22, 1);
  // Smartphone: Abstand der Gruppe zum Bildschirmrand in der Leseposition
  // (bevorzugt / mindestens, wenn es knapp wird).
  const TAP_MARGIN = 16;
  const TAP_MARGIN_MIN = 8;
  // Smartphone: Wie nah muss die Leseposition beim Anhalten sein (Anteil der
  // Bildschirmhöhe)? Voraus = man nähert sich; zurück = knapp darüber hinaus.
  const TAP_REACH_AHEAD = 0.55;
  const TAP_REACH_BACK = 0.25;
  // Smartphone: so lange Ruhe (ms), dann beginnt das Gleiten.
  const TAP_REST = 150;
  // Reicht der Platz über der schwebenden Kaufleiste nicht ganz (z. B. wenn
  // beim Zurückscrollen die Kopfleiste erscheint), darf das Bild oben um
  // höchstens diesen Anteil unter dem Rand liegen – Duftwahl, Texte und
  // Preiszeile bleiben frei. Sonst oben bündig (kleine Bildschirme, große Schrift).
  const TAP_MAX_CROP = 0.4;
  // Nur nach eigener Bewegung ausrichten – nie nach Sprüngen des Browsers
  // (Zurück-Knopf, Anker, Wiederherstellen der Scrollposition).
  const TAP_INPUT_WINDOW = 4000;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

  customElements.define(
    'venoa-scent-story',
    class VenoaScentStory extends HTMLElement {
      connectedCallback() {
        this.panels = [...this.querySelectorAll('[data-story-panel]')];
        this.frames = [...this.querySelectorAll('[data-story-frame]')];
        this.navButtons = [...this.querySelectorAll('[data-story-goto]')];
        this.pin = this.querySelector('.venoa-story__pin');
        this.framesBox = this.querySelector('.venoa-story__frames');
        this.panelBox = this.querySelector('.venoa-story__panels');
        this.bodies = this.panels.map((panel) => panel.querySelector('.venoa-story__panel-body'));
        this.frameImages = this.frames.map((frame) => frame.querySelector('img, svg'));
        this.active = 0;
        this.shown = null;
        this.scrollDir = 1;
        this.swapToken = 0;

        if (!this.panels.length || !this.pin || !('IntersectionObserver' in window)) return;

        this.controller = new AbortController();
        const { signal } = this.controller;
        this.queries = [WIDE_QUERY, WIDE_STAGE_QUERY, PHONE_QUERY, REDUCE_QUERY].map((q) => window.matchMedia(q));
        this.queries.forEach((mql) => mql.addEventListener('change', this.refresh, { signal }));
        this.lastWidth = window.innerWidth;

        this.addEventListener('focusin', this.onFocusIn, { signal });
        this.navButtons.forEach((button) => button.addEventListener('click', this.onNavClick, { signal }));
        window.addEventListener('resize', this.onResize, { passive: true, signal });
        document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });

        this.setAttribute('data-ready', '');
        this.refresh();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.teardown();
      }

      get reduced() {
        return window.matchMedia(REDUCE_QUERY).matches;
      }

      get wide() {
        return window.matchMedia(WIDE_QUERY).matches;
      }

      /* ---------- Darstellung wählen ---------- */

      teardown() {
        this.panelObserver?.disconnect();
        this.gateObserver?.disconnect();
        this.fitObserver?.disconnect();
        this.armObserver?.disconnect();
        this.textObserver?.disconnect();
        this.panelObserver = this.gateObserver = this.fitObserver = this.armObserver = this.textObserver = null;
        this.modeController?.abort();
        this.modeController = null;
        this.cancelGlide();
        window.clearTimeout(this.restTimer);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.engaged = false;
        this.aligned = false;
        this.touching = false;
        this.restPending = false;
        this.retryAheadOnly = false;
        this.removeAttribute('data-engaged');
        this.removeAttribute('data-scenes');
        this.finishSwaps();
        this.frames.forEach((frame) => frame.classList.remove('is-shown'));
        this.panels.forEach((panel) => panel.classList.remove('is-text'));
        this.panelBox?.style.removeProperty('height');
        this.style.removeProperty('--story-rest');
        this.shown = null;
      }

      refresh = () => {
        this.teardown();
        const enabled = this.hasAttribute('data-stage');
        const [, wideStage, phone] = this.queries.map((mql) => mql.matches);

        if (enabled && wideStage) {
          this.dataset.mode = 'stage';
          this.setAttribute('data-scenes', ''); // Bühnen-Layout, damit fits() richtig misst
          if (!this.fits()) delete this.dataset.mode;
        } else if (enabled && phone) {
          this.dataset.mode = 'tap';
        } else if (phone) {
          this.dataset.mode = 'flow';
        } else {
          delete this.dataset.mode;
        }

        const mode = this.dataset.mode;
        if (mode !== 'stage' && mode !== 'tap') this.removeAttribute('data-scenes');
        if (mode === 'stage' || mode === 'tap') {
          this.setAttribute('data-scenes', '');
          this.showScent(this.active, { animate: false, force: true });
          if (mode === 'stage') this.setupStage();
          else this.setupTap();
        } else {
          this.setupPanels();
          this.activate(this.active, true);
        }
      };

      /** Nur Breitenänderungen (Drehen, Fenster) richten neu ein – die Safari-
       *  Leisten ändern beim Scrollen nur die Höhe. */
      onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          if (window.innerWidth !== this.lastWidth) {
            this.lastWidth = window.innerWidth;
            this.refresh();
          } else if (this.dataset.mode === 'tap') {
            this.fitPanels(false);
          }
        }, 200);
      };

      /** Desktop: passt der längste Text neben ein ausreichend großes Bild? */
      fits() {
        const tallest = Math.max(...this.bodies.map((body) => body?.offsetHeight || 0));
        const pinStyle = getComputedStyle(this.pin);
        const inner = this.pin.clientHeight - parseFloat(pinStyle.paddingTop) - parseFloat(pinStyle.paddingBottom);
        return (this.framesBox?.clientHeight || 0) >= 320 && tallest <= inner;
      }

      /* ---------- Klassisch und Liste ---------- */

      setupPanels() {
        this.panelObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.activate(Number(entry.target.dataset.storyPanel));
            });
          },
          { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
        );
        this.panels.forEach((panel) => this.panelObserver.observe(panel));
      }

      /* ---------- Gemeinsam: Scroll-Beobachtung nur in der Nähe ---------- */

      watchScroll(onNear) {
        this.scroller = this.findScroller();
        this.lastTop = this.scroller.scrollTop;
        this.modeController = new AbortController();
        const { signal } = this.modeController;

        this.gateObserver = new IntersectionObserver(
          (entries) => {
            const near = entries[entries.length - 1].isIntersecting;
            this.near = near;
            if (near) {
              this.preloadImages();
              document.addEventListener('scroll', this.onScroll, { capture: true, passive: true, signal });
              onNear?.();
              this.onScroll();
            } else {
              document.removeEventListener('scroll', this.onScroll, { capture: true });
              this.aligned = false; // neuer Besuch darf wieder einmal ausrichten
            }
          },
          { rootMargin: '100% 0px 100% 0px' }
        );
        this.gateObserver.observe(this);
      }

      /** Desktop scrollt .page-wrapper, das Smartphone das Dokument. */
      findScroller() {
        const wrapper = document.querySelector('.page-wrapper');
        if (wrapper) {
          const overflow = getComputedStyle(wrapper).overflowY;
          if (/(auto|scroll)/.test(overflow) && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
        }
        return document.scrollingElement || document.documentElement;
      }

      get isRootScroller() {
        return this.scroller === (document.scrollingElement || document.documentElement);
      }

      get viewportTop() {
        return this.isRootScroller ? 0 : this.scroller.getBoundingClientRect().top;
      }

      get viewHeight() {
        return this.isRootScroller ? window.innerHeight : this.scroller.clientHeight;
      }

      setScrollTop(top) {
        const el = this.scroller;
        const previous = el.style.scrollBehavior;
        el.style.scrollBehavior = 'auto';
        el.scrollTop = top;
        el.style.scrollBehavior = previous;
      }

      onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(this.update);
      };

      update = () => {
        this.frame = 0;
        const top = this.scroller.scrollTop;
        if (!this.gliding && Math.abs(top - this.lastTop) > 0.5) {
          this.scrollDir = top > this.lastTop ? 1 : -1;
          this.lastUserScroll = performance.now();
        }
        this.lastTop = top;

        if (this.dataset.mode === 'stage') this.updateStage();
        if (!this.gliding) this.scheduleRest();
      };

      scheduleRest() {
        window.clearTimeout(this.restTimer);
        this.restTimer = window.setTimeout(this.onRest, this.dataset.mode === 'tap' ? TAP_REST : 170);
      }

      onRest = () => {
        if (this.gliding || this.reduced) return;
        if (this.dataset.mode === 'stage') this.restStage();
        else if (this.dataset.mode === 'tap') {
          // Liegt der Finger noch auf, wird erst nach dem Loslassen geprüft.
          if (this.touching) this.restPending = true;
          else this.restTap();
        }
      };

      /* ---------- Bühne (Desktop) ---------- */

      setupStage() {
        this.step = this.pin.offsetHeight || window.innerHeight;
        this.watchScroll();

        // Schrift nachgeladen oder vergrößert → passt es noch? Sonst klassisch.
        if ('ResizeObserver' in window) {
          this.fitObserver = new ResizeObserver(() => {
            if (this.dataset.mode === 'stage' && !this.fits()) this.refresh();
          });
          this.bodies.forEach((body) => body && this.fitObserver.observe(body));
        }
        this.updateStage();
      }

      /** Scrollposition, an der die Story (Station 0) beginnt. */
      get layoutTop() {
        return this.scroller.scrollTop + this.getBoundingClientRect().top - this.viewportTop;
      }

      station(index) {
        return this.layoutTop + index * this.step;
      }

      /** Stetiger Fortschritt: 0 = erster Duft, 1 = zweiter … */
      progress() {
        return (this.scroller.scrollTop - this.layoutTop) / this.step;
      }

      updateStage() {
        const x = this.progress();
        this.showScent(this.stageTarget(x));

        const last = this.panels.length - 1;
        const engaged = x > -0.02 && x < last + 0.02;
        if (engaged !== this.engaged) {
          this.engaged = engaged;
          this.toggleAttribute('data-engaged', engaged);
        }
      }

      /**
       * Welcher Duft wird gezeigt? Beim Gleiten das Ziel; sonst dieselbe
       * 22-%-Regel wie beim Einrasten – so zeigen Bild, Text und das
       * anschließende Gleiten immer denselben Duft.
       */
      stageTarget(x) {
        const last = this.panels.length - 1;
        if (this.gliding && this.glideIndex != null) return this.glideIndex;
        if (x <= 0) return 0;
        if (x >= last) return last;
        const lower = Math.floor(x);
        const frac = x - lower;
        if (this.scrollDir >= 0) return frac >= 0.22 ? lower + 1 : lower;
        return frac <= 0.78 ? lower : lower + 1;
      }

      restStage() {
        const plan = this.restTarget(this.progress(), this.scrollDir);
        if (!plan) return;
        // Zurück zur Mitte nur, wenn der Nutzer wirklich innehält.
        const idle = performance.now() - (this.lastUserScroll || 0);
        if (plan.patient && idle < 650) {
          this.restTimer = window.setTimeout(this.onRest, 650 - idle);
          return;
        }
        this.glideTo(this.station(plan.index), plan.index);
      }

      /** Nächste passende Leseposition in Scrollrichtung – oder null. */
      restTarget(x, dir) {
        const last = this.panels.length - 1;
        const tolerance = 0.035;

        if (x < 0) {
          if (dir > 0 && x >= -0.6) return { index: 0 };
          if (dir < 0 && x >= -0.15 && x < -tolerance) return { index: 0, patient: true };
          return null;
        }
        if (x > last) {
          if (dir < 0 && x <= last + 0.6) return { index: last };
          if (dir > 0 && x <= last + 0.15 && x > last + tolerance) return { index: last, patient: true };
          return null;
        }

        const lower = Math.floor(x);
        const frac = x - lower;
        if (frac < tolerance || frac > 1 - tolerance) return null;
        if (dir >= 0) return frac >= 0.22 ? { index: lower + 1 } : { index: lower, patient: true };
        return frac <= 0.78 ? { index: lower } : { index: lower + 1, patient: true };
      }

      /* ---------- Antippen (Smartphone) ---------- */

      setupTap() {
        this.fitPanels(false);
        this.watchScroll();
        const options = { capture: true, passive: true, signal: this.modeController.signal };

        // Solange ein Finger aufliegt, wird nie ausgerichtet (auch wenn er gerade
        // stillhält). Erst nach dem Loslassen – und nach auslaufendem Schwung –
        // wird geprüft, und nur, wenn vorher wirklich gescrollt wurde: Antippen
        // allein (Duft-Pills) bewegt die Seite nie.
        window.addEventListener(
          'touchstart',
          () => {
            this.touching = true;
            this.lastInput = performance.now();
          },
          options
        );
        const release = (event) => {
          if (event.touches?.length) return;
          this.touching = false;
          this.lastInput = performance.now();
          if (this.restPending) {
            this.restPending = false;
            this.scheduleRest();
          }
        };
        window.addEventListener('touchend', release, options);
        window.addEventListener('touchcancel', release, options);
        ['wheel', 'keydown', 'pointerdown'].forEach((type) =>
          window.addEventListener(type, () => (this.lastInput = performance.now()), options)
        );

        // Hat die Gruppe den Bildschirm ganz verlassen, darf ein erneutes
        // Erreichen wieder einmal ausrichten.
        this.armObserver = new IntersectionObserver((entries) => {
          if (entries[entries.length - 1].isIntersecting) return;
          this.aligned = false;
          this.retryAheadOnly = false;
        });
        this.armObserver.observe(this.pin);

        // Die Texte der Gruppe erscheinen gemeinsam, sobald die Gruppe ins Bild
        // kommt. Einzeln würde die Preiszeile ganz unten in der Leseposition
        // im untersten Streifen liegen, wo der allgemeine Auftritt
        // (venoa-theme.js) noch nicht auslöst – sie bliebe unsichtbar.
        this.textObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.querySelectorAll('.venoa-reveal:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
          },
          { threshold: 0.2 }
        );
        this.textObserver.observe(this.pin);

        if ('ResizeObserver' in window) {
          this.fitObserver = new ResizeObserver(() => this.fitPanels(false));
          this.bodies.forEach((body) => body && this.fitObserver.observe(body));
        }
      }

      /** Textbereich so hoch wie der gezeigte Text – weicher Höhenübergang beim Wechsel. */
      fitPanels(animate = true) {
        if (!this.panelBox) return;
        const panel = this.panels[this.shown ?? this.active];
        const height = panel?.offsetHeight || 0;
        if (!height) return;
        this.panelBox.style.transition = animate && !this.reduced ? '' : 'none';
        this.panelBox.style.height = `${height}px`;
        if (!animate || this.reduced) {
          void this.panelBox.offsetHeight;
          this.panelBox.style.removeProperty('transition');
        }
        this.fitMedia();
      }

      /**
       * Platzbedarf der Gruppe ohne Bild (Duftwahl, längster Text, Abstände,
       * Rand oben und unten) → --story-rest. Das CSS macht das Bild damit so
       * hoch, dass die ganze Gruppe in der Leseposition auf den Bildschirm passt
       * – nie kleiner als 15rem, nie größer als zuvor. Der längste Text zählt,
       * damit Bild und Duftwahl beim Wechsel stehen bleiben. Auf der
       * Produktseite zählt die schwebende Kaufleiste mit: Sie steht beim Lesen
       * unten im Bild, die Preiszeile der Story soll darüber frei bleiben.
       */
      fitMedia() {
        const nav = this.querySelector('.venoa-story__nav');
        const tallest = Math.max(0, ...this.panels.map((panel) => panel.offsetHeight || 0));
        if (!nav || !tallest) return;
        const style = getComputedStyle(this.pin);
        const gaps = 2 * (parseFloat(style.rowGap) || 0);
        const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        const pill = document.querySelector('.venoa-sticky-cta__pill');
        const bar = pill ? pill.offsetHeight + (parseFloat(getComputedStyle(pill).bottom) || 0) : 0;
        const rest = Math.ceil(padding + gaps + nav.offsetHeight + tallest + 2 * TAP_MARGIN + bar);
        this.style.setProperty('--story-rest', `${rest}px`);
      }

      /** Dwells Kopfleiste: „beim Hochscrollen“ erscheint sie, sobald die Seite nach oben geht. */
      headerInset(up) {
        const header = document.querySelector('#header-component[sticky]');
        if (!header) return 0;
        const mode = header.getAttribute('sticky');
        if (mode === 'always' || (mode === 'scroll-up' && up)) return header.getBoundingClientRect().height;
        return 0;
      }

      /**
       * Die schwebende Kaufleiste der Produktseite („Duft auswählen“) verdeckt
       * unten Platz. Gemessen wird ihre Ruhelage (offsetTop ohne Transform) –
       * so zittert das Ziel nicht, während die Leiste einschwebt.
       */
      bottomInset() {
        const vh = this.viewHeight;
        let inset = 0;
        document
          .querySelectorAll(".venoa-sticky-cta__pill[data-stuck='true']")
          .forEach((bar) => {
            if (getComputedStyle(bar).display === 'none' || !bar.offsetHeight) return;
            const top = bar.offsetTop;
            if (top > vh * 0.5 && top < vh) inset = Math.max(inset, vh - top);
          });
        return inset;
      }

      /**
       * Ruhige Leseposition: Bild, Duftwahl und Text mittig im freien Bereich
       * zwischen Kopfleiste und schwebender Kaufleiste (Rand 8–16 px).
       * Fehlt über einer Kaufleiste nur ein wenig Platz, steht die Gruppe unten
       * bündig über der Leiste (Preiszeile frei) und das Bild ist oben höchstens
       * leicht angeschnitten. Fehlt mehr (kleiner Bildschirm, große Schrift),
       * steht sie oben bündig – der Rest ist normal erreichbar. Beim Gleiten
       * nach unten verschwindet Dwells Kopfleiste, nach oben erscheint sie –
       * die Zielhöhe rechnet damit, damit nichts vom Bild verdeckt wird.
       */
      alignPlan(up = null) {
        const rect = this.pin.getBoundingClientRect();
        const vh = this.viewHeight;
        const current = this.scroller.scrollTop;
        const edge = current + (rect.top - this.viewportTop); // Gruppe stünde oben am Rand
        const height = rect.height;
        const media = this.framesBox?.offsetHeight || 0;
        const bottom = this.bottomInset();
        const place = (top) => {
          const room = vh - top - bottom;
          if (height + 2 * TAP_MARGIN_MIN <= room) {
            return { target: edge - (top + (room - height) / 2), fits: true };
          }
          const over = height + 2 * TAP_MARGIN_MIN - room;
          if (bottom && over <= media * TAP_MAX_CROP) {
            return { target: edge - (top + TAP_MARGIN_MIN - over), fits: true };
          }
          return { target: edge - (top + TAP_MARGIN), fits: false };
        };
        // Richtung einmal festlegen (up = true/false) – beim Nachführen darf
        // sie nicht kippen, sonst spränge das Ziel um die Kopfleistenhöhe.
        if (up === null) {
          const down = place(this.headerInset(false));
          if (down.target >= current - 3) return { ...down, up: false };
          return { ...place(this.headerInset(true)), up: true };
        }
        return { ...place(this.headerInset(up)), up };
      }

      /**
       * Nach dem Anhalten (Finger losgelassen, Schwung ausgelaufen): nahe der
       * Leseposition einmal weich ausrichten. Liegt sie in Scrollrichtung vor
       * einem (man nähert sich), reicht gut eine halbe Bildschirmhöhe Nähe.
       * Knapp darüber hinaus ein Viertel zurück – und nur, wenn dann die ganze
       * Gruppe sichtbar ist. Wer den Bereich verlässt, ist weiter weg und wird
       * nie zurückgeholt. Pro Besuch höchstens einmal. Das Ziel wird während
       * des Gleitens mitgeführt (Safari klappt seine Leiste ein, Bilder laden
       * nach) – die Gruppe endet genau in der Leseposition.
       */
      restTap() {
        if (this.aligned) return;
        if (performance.now() - (this.lastInput ?? -Infinity) > TAP_INPUT_WINDOW) return;
        const plan = this.alignPlan();
        const delta = plan.target - this.scroller.scrollTop;
        const distance = Math.abs(delta);
        if (distance < 3) {
          this.aligned = true;
          return;
        }
        const ahead = Math.sign(delta) === this.scrollDir;
        // Nach einer Unterbrechung nur, wenn danach wirklich weiter in Richtung
        // Leseposition gescrollt wurde (Finger nur aufgelegt → nichts).
        if (this.retryAheadOnly && (!ahead || Math.abs(this.scroller.scrollTop - this.retryFrom) < 12)) return;
        const reach = ahead ? TAP_REACH_AHEAD : plan.fits ? TAP_REACH_BACK : 0;
        if (distance > this.viewHeight * reach) return;
        this.aligned = true;
        this.glideTo(plan.target, null, () => this.alignPlan(plan.up).target);
      }

      /* ---------- Weiches Gleiten (beide Modi) ---------- */

      /**
       * target: Ziel-Scrollposition. follow (nur Smartphone): liefert das Ziel
       * laufend neu – verschiebt sich die Leseposition während des Gleitens,
       * wird weich nachgeführt, und die Bewegung endet genau dort.
       */
      glideTo(target, index = null, follow = null) {
        this.cancelGlide();
        const max = this.scroller.scrollHeight - this.viewHeight;
        const to = clamp(target, 0, Math.max(0, max));
        const from = this.scroller.scrollTop;
        const distance = to - from;
        if (Math.abs(distance) < 1 || this.reduced) {
          if (Math.abs(distance) >= 1) this.setScrollTop(to);
          this.lastTop = this.scroller.scrollTop;
          if (index != null) this.showScent(index);
          return;
        }

        this.gliding = true;
        this.glideIndex = index;
        if (index != null) this.showScent(index);
        // Desktop-Bühne: 540–900 ms je Station. Smartphone: 500–800 ms je nach Weg.
        const duration =
          this.dataset.mode === 'stage'
            ? clamp(520 + (Math.abs(distance) / this.step) * 380, 540, 900)
            : clamp(500 + (Math.abs(distance) / this.viewHeight) * 650, 500, 800);
        const start = performance.now();

        // Jede neue Eingabe übernimmt sofort.
        this.glideController = new AbortController();
        const options = { capture: true, passive: true, signal: this.glideController.signal };
        this.tapAlign = Boolean(follow);
        ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) =>
          window.addEventListener(type, this.onGlideInput, options)
        );

        // Smartphone: Dwells Kopfleiste („beim Hochscrollen“) liest die Position
        // in ihrem eigenen Frame-Callback nach jedem Scroll-Ereignis. Würde hier
        // im selben Frame geschrieben, sähe sie eine Änderung doppelt, hielte das
        // für Stillstand und blendete sich kurz aus. Deshalb: nur ganze, sich
        // ändernde Pixel und erst direkt nach dem Frame schreiben.
        const deferred = this.dataset.mode === 'tap';
        let aim = to;
        let settle = 0;
        const tick = (now) => {
          if (!this.gliding) return;
          const t = Math.min(1, (now - start) / duration);
          if (!deferred) {
            this.setScrollTop(from + distance * glideEase(t));
            if (t < 1) this.glideFrame = requestAnimationFrame(tick);
            else this.finishGlide();
            return;
          }
          const goal = follow ? clamp(follow(), 0, Math.max(0, this.scroller.scrollHeight - this.viewHeight)) : to;
          aim += (goal - aim) * 0.35;
          // Nach Ablauf der Zeit höchstens ein paar Frames Feinausrichtung.
          const exact = t >= 1 && (Math.abs(goal - aim) < 1 || ++settle > 12);
          const px = Math.round(exact ? goal : t >= 1 ? aim : from + (aim - from) * glideEase(t));
          window.clearTimeout(this.glideWrite);
          this.glideWrite = window.setTimeout(() => {
            if (!this.gliding) return;
            if (px !== Math.round(this.scroller.scrollTop)) this.setScrollTop(px);
            if (exact) this.finishGlide();
          }, 0);
          if (!exact) this.glideFrame = requestAnimationFrame(tick);
        };
        this.glideFrame = requestAnimationFrame(tick);
      }

      /**
       * Neue Eingabe während des Gleitens: sofort anhalten. War es das
       * Ausrichten auf dem Smartphone, ist es damit nicht verbraucht – hält
       * man nach weiterem Scrollen auf dem Weg zur Leseposition wieder an,
       * darf es noch einmal greifen, aber nur vorwärts (nie zurückziehend).
       */
      onGlideInput = () => {
        if (this.gliding && this.tapAlign) {
          this.aligned = false;
          this.retryAheadOnly = true;
          this.retryFrom = this.scroller.scrollTop;
        }
        this.cancelGlide();
      };

      finishGlide() {
        this.retryAheadOnly = false;
        this.gliding = false;
        this.glideIndex = null;
        this.glideController?.abort();
        this.glideController = null;
        this.lastTop = this.scroller.scrollTop;
      }

      cancelGlide = () => {
        if (!this.gliding) return;
        cancelAnimationFrame(this.glideFrame);
        window.clearTimeout(this.glideWrite);
        this.gliding = false;
        this.glideIndex = null;
        this.glideController?.abort();
        this.glideController = null;
        if (this.scroller) this.lastTop = this.scroller.scrollTop;
      };

      /* ---------- Duftwechsel: Text, Bild, Farbe, Markierung ---------- */

      showScent(index, { animate = true, force = false } = {}) {
        if (Number.isNaN(index) || index == null) return;
        if (!force && index === this.shown) return;
        const previous = this.shown;
        this.shown = index;
        this.active = index;

        this.panels.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === index);
          panel.classList.toggle('is-text', i === index);
        });
        this.navButtons.forEach((button) => {
          if (Number(button.dataset.storyGoto) === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        this.setAttribute('data-active-scent', this.panels[index]?.dataset.scent || '');

        const run = animate && !this.reduced && previous != null && previous !== index;
        this.swapImage(index, run, previous != null && index < previous ? -1 : 1);
        if (this.dataset.mode === 'tap') this.fitPanels(run);
      }

      /**
       * Bildwechsel als kurze Enthüllung: Das fertig geladene Zielbild legt sich
       * von unten (rückwärts: von oben) über das aktuelle Bild. In jedem Moment
       * ist jede Stelle genau ein Motiv – keine Überlagerung zweier Etiketten.
       * Am Ende ist nur noch das Ziel sichtbar. Beginnt ein neuer Wechsel, wird
       * ein laufender sofort abgeschlossen – höchstens zwei Motive sind je im Bild.
       */
      async swapImage(index, animate, direction) {
        const token = ++this.swapToken;
        const incoming = this.frames[index];
        if (!incoming) return;

        if (!animate) {
          this.finishSwaps();
          this.settleOn(incoming);
          return;
        }

        // Zielbild vollständig laden, bevor es erscheint (höchstens kurz warten).
        const img = incoming.querySelector('img');
        if (img && !(img.complete && img.naturalWidth)) {
          if (img.loading === 'lazy') img.loading = 'eager';
          await Promise.race([img.decode?.().catch(() => {}), new Promise((r) => setTimeout(r, 450))]);
          if (token !== this.swapToken) return;
        }

        // Laufende Enthüllung abschließen: ihr Motiv steht dann ganz, darüber
        // beginnt die neue.
        (this.swaps || []).forEach((animation) => {
          try {
            animation.finish();
          } catch {
            /* bereits beendet */
          }
        });
        this.swaps = [];
        if (this.lastIncoming && this.lastIncoming !== incoming) this.settleOn(this.lastIncoming);
        this.lastIncoming = incoming;

        incoming.style.zIndex = '3';
        incoming.classList.add('is-shown');
        const from = direction < 0 ? 'inset(0 0 100% 0)' : 'inset(100% 0 0 0)';
        const reveal = incoming.animate([{ clipPath: from }, { clipPath: 'inset(0 0 0 0)' }], {
          duration: 520,
          easing: 'cubic-bezier(0.65, 0, 0.25, 1)',
          fill: 'backwards',
        });
        const settle = img
          ? img.animate([{ scale: 1.045 }, { scale: 1 }], { duration: 760, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fill: 'backwards' })
          : null;
        this.swaps = [...(this.swaps || []), reveal, settle].filter(Boolean);

        try {
          await reveal.finished;
        } catch {
          return; // abgebrochen – ein neuerer Wechsel räumt auf
        }
        if (token !== this.swapToken) return;
        await nextFrame();
        if (token !== this.swapToken) return;
        this.settleOn(incoming);
        this.swaps = (this.swaps || []).filter((animation) => animation === settle && animation.playState === 'running');
      }

      /** Nur dieses Motiv sichtbar. */
      settleOn(frame) {
        this.frames.forEach((item) => {
          item.classList.toggle('is-shown', item === frame);
          item.style.removeProperty('z-index');
        });
        this.lastIncoming = frame;
      }

      /** Laufende Bildwechsel sofort in ihren Endzustand bringen. */
      finishSwaps() {
        (this.swaps || []).forEach((animation) => {
          try {
            animation.finish();
          } catch {
            /* bereits beendet */
          }
        });
        this.swaps = [];
        this.swapToken += 1;
      }

      /** Alle Duftbilder vorab laden, damit ein Wechsel nie auf ein Bild wartet. */
      preloadImages() {
        if (this.preloaded) return;
        this.preloaded = true;
        this.frames.forEach((frame) => {
          frame.querySelectorAll('img').forEach((img) => {
            if (img.loading === 'lazy') img.loading = 'eager';
            img.decode?.().catch(() => {});
          });
        });
      }

      /* ---------- Navigation, Tastatur, Editor ---------- */

      goTo(index) {
        const panel = this.panels[index];
        if (!panel) return;
        if (this.dataset.mode === 'stage') {
          this.glideTo(this.station(index), index);
        } else if (this.dataset.mode === 'tap') {
          this.preloadImages();
          this.showScent(index); // Scrollposition bleibt, wie sie ist
        } else {
          panel.scrollIntoView({ block: 'start', behavior: this.reduced ? 'auto' : 'smooth' });
        }
      }

      onNavClick = (event) => {
        const index = Number(event.currentTarget.dataset.storyGoto);
        if (!Number.isNaN(index)) this.goTo(index);
      };

      // Tastatur (klassischer Ablauf): Fokus in einem Panel macht dieses aktiv.
      onFocusIn = (event) => {
        if (this.hasAttribute('data-scenes')) return;
        const panel = event.target.closest?.('[data-story-panel]');
        if (panel) this.activate(Number(panel.dataset.storyPanel));
      };

      // Theme-Editor: gewählter Duft-Block wird angezeigt.
      onBlockSelect = (event) => {
        if (!(event.target instanceof Element) || !this.contains(event.target)) return;
        const panel = event.target.closest('[data-story-panel]');
        if (!panel) return;
        const index = Number(panel.dataset.storyPanel);
        if (this.hasAttribute('data-scenes')) this.goTo(index);
        else this.activate(index);
      };

      /** Klassischer Ablauf und Liste: aktives Panel (Desktop-Bild folgt). */
      activate(index, force = false) {
        if (Number.isNaN(index) || (!force && index === this.active)) return;
        this.active = index;
        const toggle = (list, attr) =>
          list.forEach((el) => el.classList.toggle('is-active', Number(el.dataset[attr]) === index));
        toggle(this.panels, 'storyPanel');
        toggle(this.frames, 'storyFrame');
        this.navButtons.forEach((button) => {
          if (Number(button.dataset.storyGoto) === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        this.setAttribute('data-active-scent', this.panels[index]?.dataset.scent || '');
      }
    }
  );
}
